import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../lib/content";
import { useLang } from "../lib/i18n";

export const Venue = () => {
  const ref = useRef(null);
  const { c } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="venue" ref={ref} className="relative h-[85svh] overflow-hidden" data-testid="venue-section">
        <motion.div style={{ y }} className="absolute -inset-y-[10%] inset-x-0">
          <img src={IMAGES.venue} alt="Loews Coral Gables Hotel" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-[#2C3552]/50" />
        </motion.div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 text-[#FAFAF1]">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.4em] mb-6 text-[#F4B8C6]">{c.venue.overline}</p>
            <h2 className="font-serif-display font-light text-5xl sm:text-6xl lg:text-7xl">
              Loews Coral <span className="italic">Gables</span>
            </h2>
            <p data-testid="venue-tagline" className="mt-6 font-serif-display italic text-lg sm:text-xl font-light text-[#FAFAF1]/90 max-w-xl mx-auto">
              {c.venue.tagline}
            </p>
            <p className="mt-4 text-sm tracking-wide font-light">2950 Coconut Grove Dr, Coral Gables, FL 33134</p>
            <a
              data-testid="venue-maps-link"
              href="https://www.google.com/maps/dir/?api=1&destination=2950+Coconut+Grove+Dr,+Coral+Gables,+FL+33134"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-8 text-[11px] uppercase tracking-[0.25em] px-8 py-3.5 rounded-full border border-[#FAFAF1]/50 hover:bg-[#FAFAF1] hover:text-[#2C3552] transition-colors duration-300"
            >
              {c.venue.directions}
            </a>
          </Reveal>
        </div>
      </section>
  );
};

export const Travel = () => {
  const { c } = useLang();
  return (
      <section id="travel" className="py-28 sm:py-40 px-6 bg-[#EEF2E3]" data-testid="travel-section">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="font-serif-display font-light text-4xl sm:text-5xl">
              {c.travel.title} <span className="italic text-[#465220]">Miami</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 border border-[#E15A70]/40 bg-[#E15A70]/10 px-6 py-5 max-w-3xl" data-testid="travel-notice">
              <p className="text-sm sm:text-base font-light leading-relaxed text-[#2C3552]">
                <span className="font-serif-display italic text-[#E15A70]">✦ </span>
                {c.travel.notice}
              </p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 mt-16">
            {c.travel.cards.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.1}>
                <div className="border-t border-[#A9B08C] pt-8" data-testid={`travel-card-${i}`}>
                  <h3 className="font-serif-display text-2xl sm:text-3xl font-medium mb-6">{t.title}</h3>
                  <ul className="space-y-4">
                    {t.lines.map((l) => (
                      <li key={l} className="text-sm sm:text-base leading-relaxed font-light text-[#2C3552]/70">
                        {l}
                      </li>
                    ))}
                  </ul>
                  {t.links?.map((lk) => (
                    <a
                      key={lk.url}
                      data-testid="travel-roomblock-link"
                      href={lk.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-fit mt-5 text-[11px] uppercase tracking-[0.2em] text-[#465220] border-b border-[#465220]/40 pb-1 hover:border-[#465220] transition-colors duration-300"
                    >
                      {lk.label}
                    </a>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
  );
};
